import React from 'react';
import { FIELD_TYPES } from '../types';

const getRequiredCheckboxOption = (field, optionIndex) => Boolean(field?.required) && optionIndex === 0;

const EMPTY_PRINT_VALUE = '—';

const getPrintValue = (field, value) => {
  if (field.type === FIELD_TYPES.CHECKBOX) {
    return value ? 'Yes' : 'No';
  }

  if (field.type === FIELD_TYPES.CHECKBOX_GROUP) {
    const selectedValues = Array.isArray(value) ? value : [];
    const selectedLabels = field.options
      ?.filter((option) => selectedValues.includes(option.value))
      .map((option) => option.label) || [];

    return selectedLabels.length ? selectedLabels.join(', ') : EMPTY_PRINT_VALUE;
  }

  if (field.type === FIELD_TYPES.SELECT) {
    const selectedOption = field.options?.find((option) => option.value === value);
    return selectedOption?.label || value || EMPTY_PRINT_VALUE;
  }

  if (value === undefined || value === null || value === '') {
    return EMPTY_PRINT_VALUE;
  }

  return String(value);
};

const PrintValue = ({ children, multiline = false }) => (
  <div className={`print-only df-print-field-value${multiline ? ' df-print-field-value--multiline' : ''}`}>
    {children || EMPTY_PRINT_VALUE}
  </div>
);

const FormField = ({ field, value, onChange, onBlur, readOnly = false }) => {
  const handleChange = (newValue) => {
    if (!readOnly) {
      onChange(field.name, newValue);
    }
  };

  const handleBlur = (event) => {
    if (!readOnly && !event.currentTarget.contains(event.relatedTarget)) {
      onBlur?.();
    }
  };

  const renderInput = () => {
    switch (field.type) {
      case FIELD_TYPES.LABEL:
        return <div className="df-static-label">{field.label}</div>;

      case FIELD_TYPES.TEXT:
        return (
          <>
            <input
              type="text"
              className="form-input df-screen-control"
              value={value || ''}
              onChange={(event) => handleChange(event.target.value)}
              placeholder={field.placeholder}
              disabled={readOnly}
              required={field.required}
            />
            <PrintValue>{getPrintValue(field, value)}</PrintValue>
          </>
        );

      case FIELD_TYPES.TEXTAREA:
        return (
          <>
            <textarea
              className="form-textarea df-screen-control"
              value={value || ''}
              onChange={(event) => handleChange(event.target.value)}
              placeholder={field.placeholder}
              disabled={readOnly}
              required={field.required}
              rows={4}
            />
            <PrintValue multiline>{getPrintValue(field, value)}</PrintValue>
          </>
        );

      case FIELD_TYPES.NUMBER:
        return (
          <>
            <input
              type="number"
              className="form-input df-screen-control"
              value={value ?? ''}
              onChange={(event) => handleChange(event.target.value)}
              placeholder={field.placeholder}
              disabled={readOnly}
              required={field.required}
            />
            <PrintValue>{getPrintValue(field, value)}</PrintValue>
          </>
        );

      case FIELD_TYPES.EMAIL:
        return (
          <>
            <input
              type="email"
              className="form-input df-screen-control"
              value={value || ''}
              onChange={(event) => handleChange(event.target.value)}
              placeholder={field.placeholder}
              disabled={readOnly}
              required={field.required}
            />
            <PrintValue>{getPrintValue(field, value)}</PrintValue>
          </>
        );

      case FIELD_TYPES.DATE:
        return (
          <>
            <input
              type="date"
              className="form-input df-screen-control"
              value={value || ''}
              onChange={(event) => handleChange(event.target.value)}
              disabled={readOnly}
              required={field.required}
            />
            <PrintValue>{getPrintValue(field, value)}</PrintValue>
          </>
        );

      case FIELD_TYPES.TIME:
        return (
          <>
            <input
              type="time"
              className="form-input df-screen-control"
              value={value || ''}
              onChange={(event) => handleChange(event.target.value)}
              disabled={readOnly}
              required={field.required}
            />
            <PrintValue>{getPrintValue(field, value)}</PrintValue>
          </>
        );

      case FIELD_TYPES.SELECT:
        return (
          <>
            <select
              className="form-select df-screen-control"
              value={value || ''}
              onChange={(event) => handleChange(event.target.value)}
              disabled={readOnly}
              required={field.required}
            >
              {field.options?.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <PrintValue>{getPrintValue(field, value)}</PrintValue>
          </>
        );

      case FIELD_TYPES.CHECKBOX:
        return (
          <div className="checkbox-item">
            <input
              className="df-screen-control"
              type="checkbox"
              id={field.id}
              checked={value || false}
              onChange={(event) => handleChange(event.target.checked)}
              disabled={readOnly}
              required={Boolean(field.required)}
            />
            <label htmlFor={field.id}>
              {field.label}
              {field.required && <span style={{ color: 'var(--error)' }}> *</span>}
            </label>
            <PrintValue>{getPrintValue(field, value)}</PrintValue>
          </div>
        );

      case FIELD_TYPES.CHECKBOX_GROUP:
        return (
          <div className="checkbox-group">
            {field.options?.map((option, optionIndex) => (
              <div key={option.value} className="checkbox-item df-screen-control">
                <input
                  type="checkbox"
                  id={`${field.id}-${option.value}`}
                  checked={(value || []).includes(option.value)}
                  onChange={(event) => {
                    const currentValues = value || [];
                    if (event.target.checked) {
                      handleChange([...currentValues, option.value]);
                    } else {
                      handleChange(currentValues.filter((entry) => entry !== option.value));
                    }
                  }}
                  disabled={readOnly}
                  required={getRequiredCheckboxOption(field, optionIndex) && !(value || []).length}
                />
                <label htmlFor={`${field.id}-${option.value}`}>{option.label}</label>
              </div>
            ))}
            <PrintValue>{getPrintValue(field, value)}</PrintValue>
          </div>
        );

      case FIELD_TYPES.TABLE:
        return (
          <div className="table-section">
            <table className="table">
              <thead>
                <tr>
                  {field.columns?.map((column) => (
                    <th key={column.key}>{column.label}</th>
                  ))}
                  {!readOnly && <th className="no-print">Actions</th>}
                </tr>
              </thead>
              <tbody>
                {(value || []).map((row, rowIndex) => {
                  const rows = value || [];

                  return (
                    <tr key={rowIndex}>
                      {field.columns?.map((column) => (
                        <td key={column.key}>
                          {column.type === 'textarea' ? (
                            <>
                              <textarea
                                className="form-textarea df-screen-control"
                                value={row[column.key] || ''}
                                onChange={(event) => {
                                  const newRows = [...rows];
                                  newRows[rowIndex] = { ...row, [column.key]: event.target.value };
                                  handleChange(newRows);
                                }}
                                disabled={readOnly}
                                rows={2}
                              />
                              <div className="print-only df-print-table-value">{row[column.key] || EMPTY_PRINT_VALUE}</div>
                            </>
                          ) : (
                            <>
                              <input
                                type="text"
                                className="form-input df-screen-control"
                                value={row[column.key] || ''}
                                onChange={(event) => {
                                  const newRows = [...rows];
                                  newRows[rowIndex] = { ...row, [column.key]: event.target.value };
                                  handleChange(newRows);
                                }}
                                disabled={readOnly}
                              />
                              <div className="print-only df-print-table-value">{row[column.key] || EMPTY_PRINT_VALUE}</div>
                            </>
                          )}
                        </td>
                      ))}
                      {!readOnly && (
                        <td className="no-print">
                          <button
                            type="button"
                            className="btn btn-danger btn-sm"
                            onClick={() => {
                              const newRows = rows.filter((_, index) => index !== rowIndex);
                              handleChange(newRows);
                            }}
                          >
                            Remove
                          </button>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {!readOnly && (
              <button
                type="button"
                className="btn btn-secondary btn-sm mt-4 no-print"
                onClick={() => {
                  const newRow = {};
                  field.columns?.forEach((column) => {
                    newRow[column.key] = '';
                  });
                  handleChange([...(value || []), newRow]);
                }}
              >
                + Add Row
              </button>
            )}
          </div>
        );

      default:
        return (
          <>
            <input
              type="text"
              className="form-input df-screen-control"
              value={value || ''}
              onChange={(event) => handleChange(event.target.value)}
              placeholder={field.placeholder}
              disabled={readOnly}
              required={field.required}
            />
            <PrintValue>{getPrintValue(field, value)}</PrintValue>
          </>
        );
    }
  };

  return (
    <div className={`form-group df-form-field-control field-type-${field.type}`} onBlur={handleBlur}>
      {field.type !== FIELD_TYPES.CHECKBOX && field.type !== FIELD_TYPES.LABEL && (
        <label className="form-label">
          {field.label}
          {field.required && <span style={{ color: 'var(--error)' }}> *</span>}
        </label>
      )}
      {renderInput()}
    </div>
  );
};

export default FormField;
